/**
 * checklistImportConfig.js
 *
 * Configuration for AI-powered checklist import feature.
 * Includes schemas, prompts, and type mappings.
 */

// ============================================================================
// CHECKLIST TYPES
// ============================================================================

export const CHECKLIST_IMPORT_TYPES = {
  EMERGENCY: 'emergency',
  PRE_DEPARTURE: 'pre_departure',
  FAMILIARISATION: 'familiarisation',
  NAVIGATION: 'navigation',
  OPERATIONAL: 'operational',
  DAILY: 'daily',
  PERIODIC: 'periodic',
  CUSTOM: 'custom'
};

export const CHECKLIST_TYPE_CONFIG = {
  [CHECKLIST_IMPORT_TYPES.EMERGENCY]: {
    id: 'emergency',
    label: 'Emergency',
    description: 'Fire, MOB, Abandon Ship, etc.',
    icon: 'AlertTriangle',
    color: 'red',
    storageKey: 'custom_emergency_checklists',
    hasExistingUI: true,
    schema: {
      hasSteps: true,
      hasConditions: true,
      hasRoles: false
    }
  },
  [CHECKLIST_IMPORT_TYPES.PRE_DEPARTURE]: {
    id: 'pre_departure',
    label: 'Pre-Departure',
    description: 'Checks before leaving berth',
    icon: 'Anchor',
    color: 'blue',
    storageKey: 'predeparture_custom_checks',
    hasExistingUI: true,
    hideFromChecklistTab: true,  // Shown as part of Navigation category, not standalone
    hideFromSettingsTab: true,   // Managed as part of Navigation category in Settings
    parentCategory: 'navigation',
    schema: {
      hasSteps: false,
      hasConditions: false,
      hasTiers: true // locked/toggle items
    }
  },
  [CHECKLIST_IMPORT_TYPES.FAMILIARISATION]: {
    id: 'familiarisation',
    label: 'Familiarisation',
    description: 'New crew induction items',
    icon: 'Users',
    color: 'green',
    storageKey: 'customFamiliarisationItems',
    hasExistingUI: true,
    hideFromChecklistTab: true,  // Has dedicated builder in Crew module
    hideFromSettingsTab: true,   // Managed via Crew module, not Checklist Settings
    schema: {
      hasSteps: false,
      hasRoles: true,
      hasSections: true
    }
  },
  [CHECKLIST_IMPORT_TYPES.NAVIGATION]: {
    id: 'navigation',
    label: 'Navigation',
    description: 'Pre-departure, heavy weather, visibility',
    icon: 'Compass',
    color: 'cyan',
    storageKey: 'custom_navigation_checklists',
    hasExistingUI: false,
    hasSubChecklists: true,
    schema: {
      hasFields: true,
      hasItems: true
    }
  },
  [CHECKLIST_IMPORT_TYPES.OPERATIONAL]: {
    id: 'operational',
    label: 'Operational',
    description: 'Anchoring, towing, transfers',
    icon: 'Settings',
    color: 'amber',
    storageKey: 'operations_checklist_templates',
    hasExistingUI: false,
    schema: {
      hasFields: true,
      hasItems: true,
      hasGroups: true
    }
  },
  [CHECKLIST_IMPORT_TYPES.DAILY]: {
    id: 'daily',
    label: 'Daily Checks',
    description: 'Routine daily inspections',
    icon: 'Calendar',
    color: 'slate',
    storageKey: 'operations_daily_checks_custom',
    hasExistingUI: true, // FT-80: Now visible in Checklists tab - uses useDailyChecks hook
    schema: {
      hasItems: true,
      isSimple: true
    }
  },
  [CHECKLIST_IMPORT_TYPES.PERIODIC]: {
    id: 'periodic',
    label: 'Periodic',
    description: 'Weekly, monthly intervals',
    icon: 'Clock',
    color: 'purple',
    storageKey: 'operations_periodic_custom',
    hasExistingUI: false,
    hideFromChecklistTab: true, // Empty - use Maintenance module instead
    schema: {
      hasItems: true,
      hasInterval: true
    }
  },
  [CHECKLIST_IMPORT_TYPES.CUSTOM]: {
    id: 'custom',
    label: 'Create New Checklist',
    description: 'Import a checklist that doesn\'t exist yet in the system',
    icon: 'PlusCircle',
    color: 'indigo',
    storageKey: 'operations_custom_templates',
    hasExistingUI: false,
    isCustomType: true,
    schema: {
      hasName: true,
      hasCategory: true,
      hasTrigger: true,
      hasItems: true
    }
  }
};

// ============================================================================
// ITEM SCHEMA
// ============================================================================

export const EXTRACTED_ITEM_SCHEMA = {
  id: '', // auto-generated
  text: '', // exact text from document
  required: true, // default to required
  regulatoryRef: null, // e.g., "WBC3 10.6"
  confidence: 0, // 0.0 - 1.0
  section: null, // grouping if detected
  notes: null, // any additional context
  originalIndex: 0 // position in source document
};

// ============================================================================
// IMPORT METADATA SCHEMA
// ============================================================================

export const IMPORT_METADATA_SCHEMA = {
  // Basic info
  id: '', // auto-generated
  checklistType: '',
  title: '',
  items: [],

  // Import tracking
  importedAt: null, // ISO timestamp
  importSource: '', // filename
  isCustom: true,

  // User identity (required for audit)
  importedBy: {
    id: '',
    name: '',
    role: ''
  },

  // Document reference (required for audit)
  sourceDocument: {
    reference: '', // SMS reference number
    revision: '',
    date: '',
    hash: '' // sha256 of original file
  },

  // Extraction metadata
  extraction: {
    method: 'ai_vision', // or 'ai_text'
    modelVersion: '',
    itemsExtracted: 0,
    itemsModifiedByUser: 0,
    confidence: 0
  },

  // Approval (required for compliance)
  approval: {
    required: true,
    approvedBy: null,
    approvedAt: null,
    signature: null // base64
  },

  // Version control
  version: 1,
  versionHistory: []
};

// ============================================================================
// AI EXTRACTION PROMPTS
// ============================================================================

export const EXTRACTION_SYSTEM_PROMPT = `You are a maritime safety document analyst extracting checklist items from company SMS (Safety Management System) documents.

CRITICAL RULES:
1. Extract text EXACTLY as written - do not paraphrase or modify wording
2. Preserve exact wording for audit compliance - this is legally important
3. Identify required vs optional items (look for "must", "shall", "ensure" = required)
4. Look for regulatory references (WBC3, SOLAS, MARPOL, COLREGs, MCA, STCW)
5. Detect section groupings if present (numbered sections, headers)
6. Mark checkbox items ([ ], ☐, ○) as checklist items
7. Ignore headers, footers, page numbers, and non-checklist content

OUTPUT: Return valid JSON matching the specified schema. No markdown, no explanations.`;

export const createExtractionPrompt = (checklistType, schema) => `Extract checklist items from this document.

TARGET TYPE: ${checklistType}
EXPECTED SCHEMA: ${JSON.stringify(schema, null, 2)}

For each item found:
- "text": Extract the EXACT text (do not modify or paraphrase)
- "required": true if item uses "must", "shall", "ensure", or appears critical
- "regulatoryRef": Any regulatory reference mentioned (e.g., "WBC3 10.6", "COLREGS Rule 19")
- "confidence": Your confidence this is a checklist item (0.0 to 1.0)
- "section": Section name/number if item is grouped
- "notes": Any relevant context

Return JSON in this format:
{
  "title": "Detected checklist title",
  "items": [
    {
      "text": "Exact item text",
      "required": true,
      "regulatoryRef": null,
      "confidence": 0.95,
      "section": "Section 1",
      "notes": null
    }
  ],
  "metadata": {
    "totalItems": 0,
    "sectionsFound": [],
    "regulatoryRefsFound": [],
    "overallConfidence": 0.0
  }
}

Return ONLY valid JSON. No markdown formatting, no explanation text.`;

// ============================================================================
// FILE HANDLING
// ============================================================================

export const SUPPORTED_FILE_TYPES = {
  'application/pdf': { ext: 'pdf', label: 'PDF' },
  'image/jpeg': { ext: 'jpg', label: 'JPG' },
  'image/png': { ext: 'png', label: 'PNG' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'docx', label: 'Word' }
};

export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// ============================================================================
// TIER LIMITS
// ============================================================================

export const TIER_LIMITS = {
  free: {
    canImport: false,
    monthlyLimit: 0,
    model: null
  },
  pro: {
    canImport: true,
    monthlyLimit: 10,
    model: 'claude-3-haiku-20240307'
  },
  enterprise: {
    canImport: true,
    monthlyLimit: Infinity,
    model: 'claude-3-opus-20240229'
  }
};

// ============================================================================
// APPROVAL REQUIREMENTS
// ============================================================================

export const APPROVAL_REQUIRED_ACTIONS = [
  'initial_import',
  'remove_item',
  'change_required_status',
  'add_regulatory_reference',
  'bulk_edit' // >3 items at once
];

export const NO_APPROVAL_ACTIONS = [
  'typo_correction',
  'reorder_items',
  'add_notes'
];

// ============================================================================
// ERROR MESSAGES
// ============================================================================

export const IMPORT_ERROR_MESSAGES = {
  FILE_TOO_LARGE: `File exceeds ${MAX_FILE_SIZE_MB}MB limit. Try compressing or splitting the document.`,
  UNSUPPORTED_FORMAT: 'Please use PDF, JPG, PNG, or DOCX format.',
  API_TIMEOUT: 'Document taking longer than expected. Please try again.',
  NO_ITEMS_FOUND: 'Could not find checklist items in this document. Try a clearer image or different page.',
  LOW_CONFIDENCE: 'Some items need review - please check highlighted items.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  TIER_LIMIT_REACHED: 'Monthly import limit reached. Upgrade to Enterprise for unlimited imports.'
};

// ============================================================================
// WIZARD STEPS
// ============================================================================

export const IMPORT_WIZARD_STEPS = [
  { id: 'select_type', label: 'Type', description: 'Choose checklist type' },
  { id: 'upload', label: 'Upload', description: 'Upload document' },
  { id: 'processing', label: 'Process', description: 'AI extraction' },
  { id: 'review', label: 'Review', description: 'Edit items' },
  { id: 'warnings', label: 'Warnings', description: 'Resolve conflicts' },
  { id: 'approval', label: 'Approve', description: 'Sign-off' },
  { id: 'confirm', label: 'Save', description: 'Confirm import' }
];
